import { badgeVariants } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { cn } from '@/lib/utils'
import { EXCHANGE_DISPLAY_NAMES } from '@/types'

interface OrderbookExchangeSelectorProps {
  exchanges: string[]
  marketType: 'spot' | 'futures'
  onToggle: (exchange: string) => void
}

export function OrderbookExchangeSelector({ exchanges, marketType, onToggle }: OrderbookExchangeSelectorProps) {
  const allOptions = useExchangeOptions('orderbook')
  const supportedOptions = useExchangeOptions('orderbook', marketType)
  const unsupportedExchanges = exchanges.filter((exchange) => !supportedOptions.includes(exchange))
  const visibleOptions = Array.from(new Set([...allOptions, ...exchanges]))
  const otherMarket = marketType === 'futures' ? 'Spot' : 'Futures'
  const hasOtherMarketOptions = allOptions.some((exchange) => !supportedOptions.includes(exchange))
  const displayName = (exchange: string) => EXCHANGE_DISPLAY_NAMES[exchange] || exchange

  return (
    <div className="space-y-2">
      <Label id="orderbook-exchanges-label">Exchanges</Label>
      <div className="flex flex-wrap gap-2" role="group" aria-labelledby="orderbook-exchanges-label">
        {visibleOptions.map((exchange) => {
          const selected = exchanges.includes(exchange)
          const supported = supportedOptions.includes(exchange)
          const marketHint = allOptions.includes(exchange) ? `${otherMarket} only` : 'Unavailable'
          return (
            <button
              key={exchange}
              type="button"
              aria-pressed={selected}
              disabled={!supported && !selected}
              className={cn(
                badgeVariants({ variant: selected ? 'default' : 'outline' }),
                'disabled:cursor-not-allowed disabled:opacity-60',
              )}
              onClick={() => onToggle(exchange)}
            >
              {displayName(exchange)}
              {!supported && <span className="ml-1 font-normal">· {marketHint}</span>}
            </button>
          )
        })}
      </div>
      {hasOtherMarketOptions && (
        <p className="text-xs text-muted-foreground">
          Switch Market Type to {otherMarket} to select {otherMarket.toLowerCase()}-only exchanges.
        </p>
      )}
      {exchanges.length === 0 && (
        <p className="text-xs text-destructive">Select at least one exchange</p>
      )}
      {unsupportedExchanges.length > 0 && (
        <p className="text-xs text-destructive">
          Unavailable for {marketType} orderbooks: {unsupportedExchanges.map(displayName).join(', ')}. Remove them or change the market.
        </p>
      )}
    </div>
  )
}
